import {
  type EnvironmentId,
  type ModelSelection,
  type LocalWorkPolicy,
  type ProjectId,
  type ProviderInteractionMode,
  type RuntimeMode,
  type ThreadId,
} from "@cleo/contracts";

import { scopeThreadRef } from "@cleo/client-runtime/environment";
import type { CreateThreadInput } from "@cleo/client-runtime/operations";

import { newThreadId } from "./lib/utils";
import { appAtomRegistry } from "./rpc/atomRegistry";
import { environmentThreadDetails } from "./state/threads";

// Arc A3 root-session birth seam (PLAN.md §Arc A3, A3-SURVEY-001.md).
//
// New Root is a distinct verb from New Thread: it dispatches exactly one
// durable `thread.create` at launch time. The laws are structural here —
// `prepareRootSessionLaunch` returns either ONE command input or a typed
// refusal, never a batch, and the launcher's in-flight guard makes a double
// submit a refusal rather than a duplicate. Addressing the created root
// happens only after the dispatcher reports acceptance; the strip then
// projects the tab from the authoritative thread stream, so a refused or
// failed launch can leave no phantom by construction.
//
// The durable "initial work frame" at this cut (declared, not silently
// reduced): the verbatim ask rides BOTH as `launchAsk` — a dedicated durable
// field that first-turn title generation cannot replace — and as the initial
// `title`, plus the World boundary and the work-mode fields. The command is
// typed as the exact wire input (`CreateThreadInput`), so the birth seam
// cannot drift from the real contract.

export interface RootSessionLaunchInput {
  readonly mode: "local" | "world";
  readonly activeWorldKey: string | null;
  readonly activeSessionId: string | null;
  readonly projectId: ProjectId;
  readonly ask: string;
  readonly now: string;
  readonly modelSelection: ModelSelection;
  readonly localWorkPolicy?: LocalWorkPolicy;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
  readonly branch: string | null;
  readonly worktreePath: string | null;
  /** Arc A5: the named parent for an explicit child or branch; null = root. */
  readonly parentThreadId?: ThreadId | null;
  /** SB3: the launching workroom; null/absent births a plain session. */
  readonly workroomKey?: string | null;
}

/** The exact wire input for one `thread.create` dispatch. */
export type RootSessionLaunchCommand = CreateThreadInput;

export type RootSessionLaunchRefusal = {
  readonly _tag: "Refused";
  readonly reason:
    | "world-required"
    | "slice-required"
    | "local-coordinate-conflict"
    | "empty-ask"
    | "launch-in-flight"
    | "no-retryable-launch";
};

export type PreparedRootSessionLaunch = {
  readonly _tag: "Launch";
  readonly input: RootSessionLaunchCommand;
};

export function prepareRootSessionLaunch(
  input: RootSessionLaunchInput,
): RootSessionLaunchRefusal | PreparedRootSessionLaunch {
  if (input.mode === "world" && !input.activeWorldKey) {
    return { _tag: "Refused", reason: "world-required" };
  }
  if (input.mode === "world" && !input.activeSessionId) {
    return { _tag: "Refused", reason: "slice-required" };
  }
  if (input.mode === "local" && (input.activeWorldKey !== null || input.activeSessionId !== null)) {
    return { _tag: "Refused", reason: "local-coordinate-conflict" };
  }
  const ask = input.ask.trim();
  if (ask.length === 0) {
    return { _tag: "Refused", reason: "empty-ask" };
  }
  return {
    _tag: "Launch",
    input: {
      threadId: newThreadId(),
      projectId: input.projectId,
      ...(input.mode === "world"
        ? { worldKey: input.activeWorldKey!, sessionId: input.activeSessionId! }
        : {}),
      title: ask,
      launchAsk: ask,
      ...(input.parentThreadId != null ? { parentThreadId: input.parentThreadId } : {}),
      ...(input.workroomKey != null ? { workroomKey: input.workroomKey } : {}),
      modelSelection: input.modelSelection,
      ...(input.mode === "local" && input.localWorkPolicy
        ? { localWorkPolicy: input.localWorkPolicy }
        : {}),
      runtimeMode: input.runtimeMode,
      interactionMode: input.interactionMode,
      branch: input.branch,
      worktreePath: input.worktreePath,
    },
  };
}

export interface RootSessionLaunched {
  readonly _tag: "Launched";
  readonly threadId: ThreadId;
}

export interface RootSessionLaunchFailed {
  readonly _tag: "Failed";
  /** True when the dispatch never settled inside the settle window, so the
   * server state is UNKNOWN: the same prepared command may be retried. */
  readonly timedOut: boolean;
  /** The identity this attempt used — reconciliation checks it against the
   * stream before claiming failure, because an ack-lost command may exist. */
  readonly threadId?: ThreadId;
}

/**
 * PLAN §A3 timeout clause at the UI layer: a dispatch that has not settled
 * inside this window is reported as failed so the form recovers. The real
 * liveness fix lives in the command effect itself
 * (THREAD_CREATE_SETTLE_TIMEOUT_MS releases the scheduler lane), which is
 * what makes the same-identity retry below reach the wire.
 *
 * Recovery is reconciliation, not a second root: EVERY failed attempt keeps
 * its prepared identity retryable until a launch succeeds.
 * `retryLastFailed` re-dispatches that ORIGINAL environment + thread
 * identity. If the first attempt never landed, it creates; if it DID land
 * (committed-but-ack-lost), the decider's require-thread-absent refusal
 * fails the re-dispatch and settlement reconciles by authoritative readback
 * of that identity — exactly-once either way.
 */
export const LAUNCH_SETTLE_TIMEOUT_MS = 15_000;

export function createRootSessionLauncher(deps: {
  readonly dispatch: (input: RootSessionLaunchCommand) => Promise<{ readonly ok: boolean }>;
  readonly settleTimeoutMs?: number;
}): {
  readonly launch: (
    input: RootSessionLaunchInput,
  ) => Promise<RootSessionLaunchRefusal | RootSessionLaunched | RootSessionLaunchFailed>;
  /**
   * Dispatch an EXACT prepared command — the adoption lane (JB4): a persisted
   * birth attempt resumes with its recorded identity instead of minting a
   * fresh one. Shares the settle window, in-flight guard, and same-identity
   * retry law with `launch`.
   */
  readonly launchPrepared: (
    input: RootSessionLaunchCommand,
  ) => Promise<RootSessionLaunchRefusal | RootSessionLaunched | RootSessionLaunchFailed>;
  /** Re-dispatch the last failed launch with its ORIGINAL identity. */
  readonly retryLastFailed: () => Promise<
    RootSessionLaunchRefusal | RootSessionLaunched | RootSessionLaunchFailed
  >;
} {
  let inFlight = false;
  let lastFailedInput: RootSessionLaunchCommand | null = null;

  const run = async (preparedInput: RootSessionLaunchCommand) => {
    inFlight = true;
    try {
      const outcome = await Promise.race([
        deps.dispatch(preparedInput),
        new Promise<null>((resolve) => {
          globalThis.setTimeout(
            () => resolve(null),
            deps.settleTimeoutMs ?? LAUNCH_SETTLE_TIMEOUT_MS,
          );
        }),
      ]);
      if (outcome === null) {
        lastFailedInput = preparedInput;
        return {
          _tag: "Failed",
          timedOut: true,
          threadId: preparedInput.threadId,
        } satisfies RootSessionLaunchFailed;
      }
      if (!outcome.ok) {
        lastFailedInput = preparedInput;
        return {
          _tag: "Failed",
          timedOut: false,
          threadId: preparedInput.threadId,
        } satisfies RootSessionLaunchFailed;
      }
      lastFailedInput = null;
      return { _tag: "Launched", threadId: preparedInput.threadId } satisfies RootSessionLaunched;
    } finally {
      inFlight = false;
    }
  };

  return {
    launch: async (input) => {
      const prepared = prepareRootSessionLaunch(input);
      if (prepared._tag === "Refused") {
        return prepared;
      }
      if (inFlight) {
        return { _tag: "Refused", reason: "launch-in-flight" };
      }
      return run(prepared.input);
    },
    launchPrepared: async (input) => {
      if (inFlight) {
        return { _tag: "Refused", reason: "launch-in-flight" };
      }
      return run(input);
    },
    retryLastFailed: async () => {
      if (inFlight) {
        return { _tag: "Refused", reason: "launch-in-flight" };
      }
      if (lastFailedInput === null) {
        return { _tag: "Refused", reason: "no-retryable-launch" };
      }
      return run(lastFailedInput);
    },
  };
}

export type RootSessionLaunchUiState = "idle" | "launching" | "unconfirmed" | "failed";

export type RootSessionLaunchOutcome =
  | RootSessionLaunched
  | RootSessionLaunchFailed
  | RootSessionLaunchRefusal;

/**
 * Honest mapping from a settled launch to the form's state. Server acceptance
 * and stream delivery are different facts: `ok` proves the durable row, only
 * the arriving shell proves the client can address it. Acceptance without
 * arrival is "unconfirmed", never "failed" — claiming "nothing was created"
 * there would be false, and a fresh retry would mint a second root.
 */
export function rootLaunchUiState(
  outcome: RootSessionLaunchOutcome,
  shellArrived: boolean,
): RootSessionLaunchUiState {
  if (outcome._tag === "Launched") return shellArrived ? "idle" : "unconfirmed";
  if (outcome._tag === "Failed") return "failed";
  return outcome.reason === "launch-in-flight" ? "launching" : "failed";
}

/**
 * Cancel/Escape are pre-dispatch verbs. While a dispatch is in flight the
 * command cannot be un-sent, so the form stays open; once it settles the form
 * may close either way — the strip projects whatever the server durably
 * created, so dismissing neither orphans nor duplicates a tab.
 */
export function launchFormDismissible(state: RootSessionLaunchUiState): boolean {
  return state !== "launching";
}

/** A retry dispatches a NEW command with a NEW thread identity. */
export function launchRetryAllowed(state: RootSessionLaunchUiState): boolean {
  return state === "idle" || state === "failed";
}

/**
 * Wait for the created root's projection to reach the client before
 * addressing it. Command acceptance proves the durable record; this proves
 * the owning stream has delivered it, so the thread route renders the real
 * root instead of aborting on an unknown thread (observed live: navigating
 * before shell arrival left the URL changed but the leaf un-transitioned).
 * A bare root has no turn/messages/session, so `threadHasStarted` cannot be
 * the predicate — existence is.
 */
/** Whether the created root's projection is already on this client: no waiting (W-4703a3f5). */
export function createdRootShellPresent(environmentId: EnvironmentId, threadId: ThreadId): boolean {
  return (
    appAtomRegistry.get(
      environmentThreadDetails.detailAtom(scopeThreadRef(environmentId, threadId)),
    ) != null
  );
}

export async function waitForCreatedRootShell(
  environmentId: EnvironmentId,
  threadId: ThreadId,
  timeoutMs = 5_000,
): Promise<boolean> {
  const threadAtom = environmentThreadDetails.detailAtom(scopeThreadRef(environmentId, threadId));
  const threadExists = () => appAtomRegistry.get(threadAtom) != null;
  if (threadExists()) return true;

  return await new Promise<boolean>((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    const finish = (result: boolean) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== null) globalThis.clearTimeout(timeoutId);
      unsubscribe();
      resolve(result);
    };
    const unsubscribe = appAtomRegistry.subscribe(threadAtom, (thread) => {
      if (thread != null) finish(true);
    });
    if (threadExists()) {
      finish(true);
      return;
    }
    timeoutId = globalThis.setTimeout(() => finish(false), timeoutMs);
  });
}

/** Route target for an accepted launch — addressing happens only here. */
export function launchedRootRoute(
  environmentId: EnvironmentId,
  threadId: ThreadId,
): {
  readonly to: "/$environmentId/$threadId";
  readonly params: { readonly environmentId: EnvironmentId; readonly threadId: ThreadId };
} {
  return { to: "/$environmentId/$threadId", params: { environmentId, threadId } };
}

export type { ProjectId };
